import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import datetime from "config/datetime";
import i18n from "config/i18n";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import useLocalesList, { type LocaleOption } from "hooks/useLocalesList";
import moment from "moment";

interface ProvisoryProfile {
  preferences: {
    locale: string;
  };
}

export interface LocaleType {
  locale: string;
  locales: LocaleOption[];
}

export const LocaleContext = createContext<LocaleType | undefined>(undefined);
LocaleContext.displayName = "LocaleContext";

export interface LocaleActionsType {
  onChangeLocale: (locale: string) => void;
}

export const LocaleActionsContext = createContext<LocaleActionsType | undefined>(undefined);
LocaleActionsContext.displayName = "LocaleActionsContext";

interface LocaleProviderProps {
  children?: React.ReactNode;
}

function LocaleProvider(props: LocaleProviderProps) {
  const [profile, setProfile] = useState<ProvisoryProfile>({
    preferences: { locale: LocalStorageHelper.get(LocalStorageHelper.keys.profileLocale) || "en" },
  });

  const locales = useLocalesList();

  const onChangeLocale = useCallback((newLocale: string) => {
    setProfile({ preferences: { locale: newLocale } });
    LocalStorageHelper.set(LocalStorageHelper.keys.profileLocale, JSON.stringify(newLocale));
    moment.locale(newLocale.toLowerCase());
    datetime.locale(newLocale.toLowerCase());
    i18n.changeLanguage(newLocale);
    // Keep the document language in sync for assistive tech (WCAG 3.1.1 Language of Page).
    document.documentElement.lang = newLocale;
  }, []);

  useEffect(() => {
    /**
     * If the language is different from the one in the profile, change it.
     * This should run only once, when the app is loaded.
     */
    if (i18n.language !== profile.preferences.locale) {
      i18n.changeLanguage(profile.preferences.locale);
    }
    moment.locale(profile.preferences.locale.toLowerCase());
    datetime.locale(profile.preferences.locale.toLowerCase());
    // Keep the document language in sync for assistive tech (WCAG 3.1.1 Language of Page).
    document.documentElement.lang = profile.preferences.locale;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      locale: profile.preferences.locale,
      locales: locales,
    }),
    [profile.preferences.locale, locales]
  );

  const actions = useMemo(() => ({ onChangeLocale }), [onChangeLocale]);

  return (
    <LocaleActionsContext.Provider value={actions}>
      <LocaleContext.Provider value={value}>{props.children}</LocaleContext.Provider>
    </LocaleActionsContext.Provider>
  );
}

export default LocaleProvider;
